import { ChangeDetectionStrategy, Component, inject, OnInit } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { FieldArrayType, FieldArrayTypeConfig } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { CaseTypesState } from "@vp/data-access/case-types";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import { CaseType, Organization, Override, Tag } from "@vp/models";
import { ConfirmationDialogComponent } from "@vp/shared/components/confirmation-dialog";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from "rxjs";
import { map, switchMap, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { AddFormComponent } from "../add-form/add-form.component";

interface PageParams {
  tagId: string;
}

@Component({
  selector: "vp-edit-case-cost",
  standalone: false,
  templateUrl: "./edit-case-cost.component.html",
  styleUrls: ["./edit-case-cost.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCaseCostComponent extends FieldArrayType<FieldArrayTypeConfig> implements OnInit {
  allCaseTypes$: Observable<CaseType[]> = inject(Store).select(CaseTypesState.allCaseTypes);
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  tagState$: Observable<Tag | null> = inject(Store).select(TagsState.tagState);
  selectedTag$: Observable<Tag | null> = inject(Store).select(TagManagerState.selectedTag);

  private readonly destroy$ = new Subject();
  private readonly _schema$ = new BehaviorSubject<any>(null);
  private readonly _dataSource$ = new BehaviorSubject<MatTableDataSource<Override> | null>(null);

  dataSource$ = this._dataSource$.asObservable();
  schema$ = this._schema$.asObservable();

  constructor(
    private readonly notificationService: NotificationService,
    public store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly matDialog: MatDialog
  ) {
    super();
  }

  costOverrides$ = this.selectedTag$.pipe(
    filterNullMap(),
    map((tag: Tag) => {
      if (
        tag.tagData &&
        Object.keys(tag.tagData).includes("costOverrides") &&
        Array.isArray(tag.tagData.costOverrides)
      ) {
        const allCaseTypeIds = this.store
          .selectSnapshot(CaseTypesState.allCaseTypes)
          .map(caseType => caseType.caseTypeId);
        const costOverrides = tag.tagData.costOverrides as Override[];
        return costOverrides.filter(c => allCaseTypeIds.includes(c.caseTypeId));
      }
      return [];
    }),
    takeUntil(this.destroy$)
  );

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        map((paramMap: ParamMap) => {
          return { tagId: paramMap.get("tagId") || null } as PageParams;
        }),
        withLatestFrom(this.tags$, this.organization$.pipe(filterNullMap())),
        map(([pageParam, tags, organization]: [PageParams, Tag[], Organization]) => {
          const currentTag = tags.find(t => t.tagId === pageParam.tagId) as Tag;
          const tagType = organization.tagTypes.find(x => x.tagTypeId === currentTag?.tagTypeId);
          this._schema$.next(tagType?.tagSchema?.properties?.costOverrides);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  filteredCaseTypes = (): CaseType[] => {
    const allCaseTypes = this.store.selectSnapshot(CaseTypesState.allCaseTypes);
    const costOverrides =
      (this.store.selectSnapshot(TagManagerState.selectedTag)?.tagData
        ?.costOverrides as Override[]) ?? [];

    if (costOverrides && costOverrides.length) {
      return allCaseTypes.filter(
        caseType =>
          costOverrides.findIndex(override => override.caseTypeId === caseType.caseTypeId) === -1
      );
    }

    return allCaseTypes;
  };

  addOverride() {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            this.matDialog
              .open(AddFormComponent, {
                disableClose: true,
                data: {
                  allCaseTypes: this.filteredCaseTypes(),
                  override: {},
                  mode: "create"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        filterNullMap()
      )
      .subscribe({
        next: ([tag, override]: [Tag, Override[]]): void => {
          this.store.dispatch(new TagManagerActions.PatchCostOverride(tag.tagId, override));
        }
      });
  }

  updateDataSource(currentTag: Tag) {
    if (currentTag.tagData) {
      this._dataSource$.next(
        new MatTableDataSource(currentTag.tagData.costOverrides as Override[])
      );
    }
  }

  updateOverride(override: Override) {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            this.matDialog
              .open(AddFormComponent, {
                disableClose: true,
                data: {
                  allCaseTypes: this.store.selectSnapshot(CaseTypesState.allCaseTypes),
                  override: override,
                  mode: "edit"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        tap(([tag, override]: [Tag, Override[]]) => {
          this.store.dispatch(new TagManagerActions.PatchCostOverride(tag.tagId, override));
        })
      )
      .subscribe();
  }

  deleteOverride(caseTypeId: string) {
    const mes = `Are you sure you want to delete this cost override?`;
    this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: "Are you sure?",
          message: mes
        },
        panelClass: "mat-mdc-dialog-confirm"
      })
      .afterClosed()
      .pipe(
        withLatestFrom(this.selectedTag$),
        filterNullMap(),
        take(1),
        switchMap(([dialogResult, tag]: [any, Tag | null]) => {
          if (dialogResult && tag) {
            return this.store.dispatch(
              new TagManagerActions.DeleteCostOverride(tag.tagId, caseTypeId)
            );
          } else {
            return EMPTY;
          }
        })
      )
      .subscribe({
        next: () => this.notificationService.successMessage("Deleted successfully"),
        error: e => {
          if (e.status === 403) {
            this.notificationService.errorMessage(
              "You do not have permission to delete this cost override."
            );
            return;
          }
          this.notificationService.errorMessage("Delete failed.");
        }
      });
  }
}
